import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from "@angular/core";
import { Router } from '@angular/router';
import _ from 'lodash';

import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { AuthService } from "@Services/auth.service";
import { ReportsService } from "@Services/reports.service";
import { environment } from 'src/environments/environment';
import { NgxPermissionsService } from "ngx-permissions";

@Component({
    standalone: false,
    selector: 'sm-main-menu',
    templateUrl: './sm-main-menu.component.html',
    styleUrls: ['./sm-main-menu.component.scss']
})
export class SmMainMenuComponent implements OnInit {

    @HostBinding('class.aurora-enabled') public readonly auroraEnabled = environment.auroraV1 === 'true';

    @Input() view: string;
    @Output() itemSelected = new EventEmitter<void>();

    public menuItems: AuroraMenuItem[];
    public orderedMenuItems: MainMenuItemVM[];
    private readonly selectedItemIcon = 'checkmark';

    constructor(
        public router: Router,
        private permissionsService: NgxPermissionsService,
        private authService: AuthService,
        private reportsService: ReportsService)
    { }

    ngOnInit() {
        this.orderedMenuItems = [];
        this.menuItems = [];

        if (this.view != 'Admin') {
            this.addMenuItem({
                Label: "Work",
                RouteUrl: "Work",
                Order: 0,
                IsExternal: false
            });
        }

        this.permissionsService.getPermissions();
        let configureMenu = this.permissionsService.hasPermission([
            TenantPermissions.Common_SystemAccess,
            TenantPermissions.Common_Tenant,
            TenantPermissions.Common_Programs,
        ]);

        let manageMenu = this.permissionsService.hasPermission([
            TenantPermissions.AdvancedWorkflows_TeamManagement
        ]);

        manageMenu.then(result => {
            if (result) {
                this.addMenuItem({
                    Label: "Manage",
                    RouteUrl: "Mgmt",
                    Order: 1,
                    IsExternal: false,
                    Disabled: !this.authService.isProgramManager(),
                    Title: this.authService.isProgramManager() ? null : 'You currently are not listed as a manager for any programs.'
                });
            }
        })


        configureMenu.then(result => {
            if (result) {
                this.addMenuItem({
                    Label: "Configure",
                    RouteUrl: "Conf",
                    Order: 2,
                    IsExternal: false
                });
            }
        });

        if (this.authService.spaName === 'work')
        {
            let reportsPermissions = this.permissionsService.hasPermission([
                TenantPermissions.Base_Work_Reports,
            ]);

            reportsPermissions.then(result => {
                if (result) {
                    this.addMenuItem({
                        Label: "Reports",
                        RouteUrl: "reports",
                        Order: 3,
                        IsExternal: true
                    });
                }
            });
        }
    }

    addMenuItem(menuItem: MainMenuItemVM) {
        this.orderedMenuItems.push(menuItem);
        this.orderedMenuItems = _.sortBy(this.orderedMenuItems, m => m.Order);
        this.syncAuroraMenuItems();
    }

    public onMenuItemClick(event: CustomEvent<{ item: AuroraMenuItem }>) {
        const menuItem = this.orderedMenuItems.find(item => item.RouteUrl === event?.detail?.item?.id);
        if (!menuItem || menuItem.Disabled) {
            return;
        }

        this.itemClick(menuItem);
    }

    public isActive(menuItem: MainMenuItemVM): boolean {
        return this.view == menuItem.RouteUrl;
    }

    public itemClick(menuItem: MainMenuItemVM) {
        if (menuItem.RouteUrl == "reports"){
            this.reportsService.openReports();
            this.itemSelected.emit();
            return;
        }

        if (menuItem.RouteUrl == 'documentation') {
            window.open(environment.ikcDocumentationUrl);
            this.itemSelected.emit();
            return;
        }

        this.router.navigate([menuItem.RouteUrl]);
        this.itemSelected.emit();
    }

    private syncAuroraMenuItems(): void {
        this.menuItems = this.orderedMenuItems.map((item) => ({
            id: item.RouteUrl,
            label: item.Label,
            disabled: item.Disabled,
            supportingText: item.Disabled ? item.Title : undefined,
            selected: this.isActive(item),
            icon: this.isActive(item) ? this.selectedItemIcon : undefined
        }));
    }
};

interface MainMenuItemVM {
    Label: string;
    RouteUrl: string;
    Order: number;
    IsExternal: boolean;
    Title?: string;
    Disabled?: boolean;
}

interface AuroraMenuItem {
    id: string;
    label: string;
    disabled?: boolean;
    supportingText?: string;
    selected?: boolean;
    icon?: string;
}
